import { useEffect, useState } from "react";
import styled from "styled-components";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled";
import axios from "../../../axios";
import { getAllTeachers } from "../../../helpers/getAllTeachers";
import { colors } from "../../../styles/theme/tokens";
import * as S from "../Funnel/Funnel.styled";

// «Бонуси вчителям»: +100 грн за продовження пакета (нараховується сам при оплаті «Пакет» вчителю з картки учня)
// і ручні бонуси. Адмін бачить усе за період, додає, змінює, видаляє. Дані: /admin/teacher-bonuses
const fmt = (n) => Math.round(n).toLocaleString("uk-UA");
const day = (d) => new Date(d).toLocaleDateString("uk-UA", { day: "numeric", month: "short" });
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
// Звітний період школи — з 9-го по 8-ме, як в «Оплата учнів»
const periodStart = (offset) => {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() - (now.getDate() < 9 ? 1 : 0), 9);
  return new Date(base.getFullYear(), base.getMonth() + offset, 9);
};
const EMPTY = { teacherId: "", studentCode: "", amount: "100", date: iso(new Date()), note: "" };

export default function TeacherBonuses() {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [form, setForm] = useState(null);       // null — форма закрита; { id? , ...поля }
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const from = periodStart(offset);
  const to = periodStart(offset + 1);
  const load = () => {
    setError(false);
    axios.get(`admin/teacher-bonuses?from=${iso(from)}&to=${iso(to)}`).then((res) => setData(res.data)).catch(() => setError(true));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [offset]);
  useEffect(() => {
    getAllTeachers().then((list) => setTeachers(list.filter((t) => !t.accessBlocked).sort((a, b) => a.teacherId - b.teacherId))).catch(() => {});
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const openNew = () => { setFormError(""); setForm({ ...EMPTY }); };
  const openEdit = (b) => {
    setFormError("");
    setForm({ id: b._id, teacherId: String(b.teacherId), studentCode: b.studentCode || "", amount: String(b.amount), date: iso(new Date(b.date)), note: b.note || "" });
  };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError("");
    const { id, ...body } = form;
    try {
      if (id) await axios.patch(`admin/teacher-bonuses/${id}`, body);
      else await axios.post("admin/teacher-bonuses", body);
      setForm(null);
      load();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Не вдалося зберегти");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (b) => {
    if (!window.confirm(`Видалити бонус ${b.amount} грн для ${b.teacherName}?`)) return;
    try {
      await axios.delete(`admin/teacher-bonuses/${b._id}`);
      load();
    } catch {
      window.alert("Не вдалося видалити");
    }
  };

  return (
    <PagesWrapper>
      <TopPageTitle value="Бонуси вчителям" />
      <S.Page>
        <S.Card>
          <S.CardHead>
            <div>
              <S.H>Період {day(from)} – {day(new Date(to.getTime() - 864e5))}</S.H>
              <S.Hint>+100 грн нараховується сам, коли учень продовжує пакет кнопкою «Пакет» в «Оплата учнів» — вчителю з картки учня. Бонуси входять у зарплатний баланс вчителя і «Виплату вчителям».</S.Hint>
            </div>
            <S.Chips>
              <S.Chip onClick={() => setOffset(offset - 1)} aria-label="Попередній період">←</S.Chip>
              <S.Chip onClick={() => setOffset(offset + 1)} disabled={offset >= 0} aria-label="Наступний період">→</S.Chip>
            </S.Chips>
          </S.CardHead>
          {error && <S.ErrorText>Не вдалося завантажити бонуси</S.ErrorText>}
          {data && (
            <S.Stats>
              <S.Stat><S.Big>{fmt(data.total)} грн</S.Big><S.Small>усього бонусів</S.Small></S.Stat>
              <S.Stat><S.Big>{data.count}</S.Big><S.Small>нарахувань</S.Small></S.Stat>
              {data.byTeacher.map((t) => (
                <S.Stat key={String(t.teacherId)}><S.Big>{fmt(t.amount)} грн</S.Big><S.Small>{t.teacherName}{t.teacherCode ? ` (ID ${t.teacherCode})` : ""} · {t.count}</S.Small></S.Stat>
              ))}
            </S.Stats>
          )}
        </S.Card>

        <S.Card>
          <S.CardHead>
            <S.H>Нарахування</S.H>
            {!form && <S.Btn onClick={openNew}>+ Додати бонус</S.Btn>}
          </S.CardHead>

          {form && (
            <S.FormBox onSubmit={save}>
              <S.FormRow>
                <label>
                  Учень (ID, необовʼязково)
                  <S.Input value={form.studentCode} onChange={set("studentCode")} placeholder="напр. 52" inputMode="numeric" />
                </label>
                <label>
                  Вчитель {form.id ? "" : "(порожньо — з картки учня)"}
                  <S.Select value={form.teacherId} onChange={set("teacherId")}>
                    <option value="">—</option>
                    {teachers.map((t) => <option key={t._id} value={t._id}>{t.name} (ID {t.teacherId})</option>)}
                  </S.Select>
                </label>
                <label>
                  Сума, грн
                  <S.Input value={form.amount} onChange={set("amount")} inputMode="numeric" />
                </label>
                <label>
                  Дата (період ЗП)
                  <S.Input type="date" value={form.date} onChange={set("date")} />
                </label>
              </S.FormRow>
              <label>
                Коментар (бачить вчитель)
                <S.Input value={form.note} onChange={set("note")} placeholder="напр. продовження внесли ручним +8" maxLength={300} />
              </label>
              {formError && <S.ErrorText>{formError}</S.ErrorText>}
              <Buttons>
                <S.Btn type="submit" disabled={saving}>{form.id ? "Зберегти" : "Додати"}</S.Btn>
                <S.Btn type="button" $ghost onClick={() => setForm(null)}>Скасувати</S.Btn>
              </Buttons>
            </S.FormBox>
          )}

          {data && !data.items.length && <S.Empty>У цьому періоді бонусів ще немає.</S.Empty>}
          {data?.items.map((b) => (
            <Row key={b._id}>
              <div>
                <b>{b.teacherName}</b>{b.teacherCode ? ` (ID ${b.teacherCode})` : ""} · +{fmt(b.amount)} грн
                <Sub>
                  {day(b.date)} · {b.source === "auto" ? `продовження: ${b.studentName}${b.studentCode ? ` (ID ${b.studentCode})` : ""}, пакет ${b.lessons} ур.` : `вручну${b.studentName ? `: ${b.studentName}${b.studentCode ? ` (ID ${b.studentCode})` : ""}` : ""}`}
                  {b.note ? ` · ${b.note}` : ""}
                </Sub>
              </div>
              <Actions>
                <S.LinkBtn onClick={() => openEdit(b)}>змінити</S.LinkBtn>
                <S.LinkBtn onClick={() => remove(b)}>видалити</S.LinkBtn>
              </Actions>
            </Row>
          ))}
        </S.Card>
      </S.Page>
    </PagesWrapper>
  );
}

const Row = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 10px 0; border-bottom: 1px solid ${colors.line}; font-size: .92rem;
`;
const Sub = styled.div`font-size: .8rem; color: ${colors.muted}; margin-top: 2px;`;
const Actions = styled.div`display: flex; gap: 14px;`;
const Buttons = styled.div`display: flex; gap: 8px;`;
